/**
 * BulkSkipReasonModal.js
 *
 * FT-283 §5.4: collects ONE bulk-skip reason for every incomplete check on the
 * active operation. Mounted from VoyageDocument when the user taps the green
 * "Complete operation" button and one-or-more checks remain unticked /
 * unskipped.
 *
 * Minimal surface:
 *   - Title:   "Some checks were not completed"
 *   - List:    incompleteChecks.map(c => c.text || c.label)
 *   - Field:   single textarea, required, minLength=3 (after trim)
 *   - Actions: Cancel (no state change) / Confirm (returns reason string)
 *
 * No per-check reason, no time-picker (the parent already captured the event
 * time inline above the Complete button).
 *
 * Contract:
 *   <BulkSkipReasonModal
 *     isOpen={bulkSkipReasonOpen}
 *     incompleteChecks={[{ id, text|label }, ...]}
 *     onCancel={() => { ... release in-flight guard }}
 *     onConfirm={(reason) => { ... bulkSkipAndComplete(...) }}
 *   />
 */

import React, { useState, useEffect } from 'react';
import { X, AlertTriangle } from 'lucide-react';

const BulkSkipReasonModal = ({
  isOpen,
  incompleteChecks = [],
  onCancel,
  onConfirm
}) => {
  const [reason, setReason] = useState('');

  // Reset when the modal opens.
  useEffect(() => {
    if (isOpen) setReason('');
  }, [isOpen]);

  if (!isOpen) return null;

  const trimmed = reason.trim();
  const canConfirm = trimmed.length >= 3;

  const handleConfirm = () => {
    if (!canConfirm) return;
    onConfirm?.(trimmed);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onCancel} />

      {/* Modal */}
      <div className="relative w-full max-w-lg bg-white rounded-t-2xl sm:rounded-2xl shadow-xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200 bg-gray-900">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-500 rounded-lg">
              <AlertTriangle className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">Some checks were not completed</h2>
              <p className="text-sm text-slate-300">
                {incompleteChecks.length} check{incompleteChecks.length === 1 ? '' : 's'} will be marked skipped
              </p>
            </div>
          </div>
          <button
            onClick={onCancel}
            className="p-2 hover:bg-slate-700 rounded-full transition-colors"
            aria-label="Cancel"
          >
            <X className="h-5 w-5 text-slate-300" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto flex-1 space-y-4">
          {/* List of incomplete checks */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Checks to be skipped
            </label>
            <ul className="border border-slate-200 rounded-lg divide-y divide-slate-100 bg-slate-50">
              {incompleteChecks.map((c) => (
                <li
                  key={c.id || c.text || c.label}
                  className="px-3 py-2 text-sm text-slate-700"
                >
                  {c.text || c.label || 'Unnamed check'}
                </li>
              ))}
            </ul>
          </div>

          {/* Reason input */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Reason for skipping <span className="text-red-500">*</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why were these checks not completed? (3+ characters)"
              rows={3}
              minLength={3}
              autoFocus
              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent resize-none text-base"
            />
            <p className="text-xs text-slate-500 mt-1">
              Applied to every skipped check in this operation.
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3 p-4 border-t border-slate-200 bg-slate-50">
          <button
            onClick={onCancel}
            className="flex-1 py-3 px-4 min-h-[56px] border border-slate-300 text-slate-700 rounded-lg font-medium hover:bg-slate-100 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!canConfirm}
            className="flex-1 py-3 px-4 min-h-[56px] bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkSkipReasonModal;
